import { useState, useEffect } from 'react';
import { Newspaper, Plus, Edit, Trash2, X, Loader2 } from 'lucide-react';

export default function NoticiasAdmin() {
  const [noticias, setNoticias] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [noticiaSeleccionada, setNoticiaSeleccionada] = useState(null);

  useEffect(() => { cargarNoticias(); }, []);

  const cargarNoticias = async () => {
    try {
      const res = await fetch('https://logia-production-725b.up.railway.app/api/noticias');
      const data = await res.json();
      if (data.success) setNoticias(data.data);
    } catch (error) { console.error("Error:", error); }
  };

  const abrirModal = (noticia = null) => {
    setNoticiaSeleccionada(noticia);
    setIsModalOpen(true);
  };

  // Crear (POST) y Actualizar (PUT)
  const handleGuardar = async (formDataState) => {
    const url = noticiaSeleccionada 
      ? `https://logia-production-725b.up.railway.app/api/admin/noticias/${noticiaSeleccionada.id}`
      : 'https://logia-production-725b.up.railway.app/api/admin/noticias';
    const method = noticiaSeleccionada ? 'PUT' : 'POST';

    // Usar FormData para soportar archivos
    const formData = new FormData();
    formData.append('titulo', formDataState.titulo);
    formData.append('articulo', formDataState.articulo);
    if (formDataState.foto) {
      formData.append('foto', formDataState.foto);
    }

    try {
      const res = await fetch(url, {
        method,
        // No se establece Content-Type, el navegador lo asigna automáticamente con el boundary correcto para archivos
        body: formData
      });
      const data = await res.json();
      if (data.success) {
        setIsModalOpen(false);
        cargarNoticias();
      } else alert('Error al guardar: ' + data.error);
    } catch (error) { alert('Error de conexión.'); }
  };

  // Eliminar (DELETE)
  const handleEliminar = async (id) => {
    if (!window.confirm("¿Estás seguro de eliminar esta noticia? Esta acción es irreversible.")) return;
    try {
      const res = await fetch(`https://logia-production-725b.up.railway.app/api/admin/noticias/${id}`, {
        method: 'DELETE'
      });
      const data = await res.json();
      if (data.success) {
        cargarNoticias();
      } else alert('Error al eliminar: ' + data.error);
    } catch (error) { alert('Error de conexión.'); }
  };

  return (
    <div className="flex flex-col gap-6 animate-fade-in relative">
      <div className="flex justify-between items-center bg-[#0a0a0a] border border-white/10 p-6 shadow-lg relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-10 mix-blend-overlay pointer-events-none"></div>
        <div className="relative z-10">
          <h1 className="font-serif text-3xl md:text-4xl text-[#C5A059] m-0 tracking-wide flex items-center gap-3">
            <Newspaper size={32} /> Gestión de Noticias
          </h1>
          <p className="text-gray-400 font-light text-sm mt-2 m-0">Publica boletines y novedades de la Logia.</p>
        </div>
        <button onClick={() => abrirModal()} className="relative z-10 flex items-center gap-2 px-6 py-3 text-xs tracking-widest uppercase font-bold bg-[#C5A059] text-[#050505] hover:bg-white transition-colors">
          <Plus size={16} /> Redactar Noticia
        </button>
      </div>

      <div className="overflow-x-auto bg-[#050505] border border-white/5">
        <table className="w-full text-left border-collapse min-w-[800px]">
          <thead>
            <tr className="border-b border-white/10 bg-white/5">
              <th className="py-4 px-6 text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Fecha</th>
              <th className="py-4 px-6 text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Foto</th>
              <th className="py-4 px-6 text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Título</th>
              <th className="py-4 px-6 text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {noticias.map((noticia) => (
              <tr key={noticia.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                <td className="py-4 px-6 text-gray-400 text-xs font-mono">{new Date(noticia.fecha_creacion).toLocaleDateString()}</td>
                <td className="py-4 px-6">
                  {noticia.foto_url ? <img src={noticia.foto_url} alt="cover" className="w-12 h-12 object-cover rounded" /> : '-'}
                </td>
                <td className="py-4 px-6 text-white text-sm font-medium">{noticia.titulo}</td>
                <td className="py-4 px-6 text-right flex justify-end gap-2">
                  <button onClick={() => abrirModal(noticia)} className="text-gray-500 hover:text-[#C5A059] transition-colors p-2" title="Editar"><Edit size={16} /></button>
                  <button onClick={() => handleEliminar(noticia.id)} className="text-gray-500 hover:text-red-500 transition-colors p-2" title="Eliminar"><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isModalOpen && <NoticiaModal noticiaData={noticiaSeleccionada} onClose={() => setIsModalOpen(false)} onSave={handleGuardar} />}
    </div>
  );
}

function NoticiaModal({ noticiaData, onClose, onSave }) {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    titulo: noticiaData?.titulo || '',
    articulo: noticiaData?.articulo || '',
    foto: null
  });

  const handleSubmit = async (e) => { 
    e.preventDefault(); 
    setIsLoading(true); 
    await onSave(formData); 
    setIsLoading(false); 
  };

  return (
    <div className="fixed inset-0 z-[999] flex justify-center items-start pb-10 px-4 sm:px-6 bg-[#050505]/90 backdrop-blur-sm">
      <div className="bg-[#0a0a0a] border border-[#C5A059]/30 w-full max-w-2xl shadow-2xl relative flex flex-col max-h-[calc(100vh-160px)] animate-fade-in">
        <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#C5A059] to-transparent opacity-50 shrink-0"></div>
        
        <div className="flex justify-between items-center p-5 md:p-6 border-b border-white/10 shrink-0">
          <h2 className="font-serif text-xl md:text-2xl text-white m-0">{noticiaData ? 'Editar Noticia' : 'Redactar Noticia'}</h2>
          <button onClick={onClose} disabled={isLoading} className="bg-transparent border-none text-gray-500 hover:text-white cursor-pointer p-1"><X size={20} /></button>
        </div>
        
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="p-5 md:p-6 overflow-y-auto flex-1 flex flex-col gap-4 hide-scrollbar">
            <div className="flex flex-col gap-2">
              <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Titular</label>
              <input type="text" value={formData.titulo} onChange={e => setFormData({...formData, titulo: e.target.value})} required disabled={isLoading} className="bg-[#050505] border border-white/10 text-white px-3 py-3 text-sm focus:outline-none focus:border-[#C5A059] disabled:opacity-50" />
            </div>
            
            <div className="flex flex-col gap-2">
              <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Imagen Principal {noticiaData && '(Opcional)'}</label>
              
              {/* VISTA PREVIA DE IMAGEN ACTUAL */}
              {noticiaData?.foto_url && (
                <div className="flex items-center gap-4 mb-2 p-3 bg-black/30 border border-white/10 rounded">
                  <img src={noticiaData.foto_url} alt="Actual" className="w-16 h-16 object-cover rounded border border-white/20" />
                  <div className="flex flex-col">
                    <span className="text-xs text-green-400 font-semibold">✓ Imagen actual guardada</span>
                    <span className="text-[10px] text-gray-500">Si dejas el campo vacío abajo, se conservará esta imagen.</span>
                  </div>
                </div>
              )}

              <input type="file" accept="image/*" onChange={e => setFormData({...formData, foto: e.target.files[0]})} required={!noticiaData} disabled={isLoading} className="text-gray-400 text-sm file:mr-4 file:py-2 file:px-4 file:border-0 file:text-xs file:font-semibold file:bg-[#C5A059] file:text-[#050505] hover:file:bg-white disabled:opacity-50" />
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Contenido del Artículo</label>
              <textarea value={formData.articulo} onChange={e => setFormData({...formData, articulo: e.target.value})} required disabled={isLoading} rows="8" className="bg-[#050505] border border-white/10 text-white px-3 py-3 text-sm focus:outline-none focus:border-[#C5A059] resize-none disabled:opacity-50"></textarea>
            </div>
          </div>
          
          <div className="p-5 border-t border-white/10 flex justify-end gap-3 shrink-0 bg-[#050505]">
            <button type="button" onClick={onClose} disabled={isLoading} className="bg-transparent border border-white/20 text-gray-300 px-6 py-3 text-xs tracking-widest uppercase hover:bg-white/5 transition-colors disabled:opacity-50">Cancelar</button>
            <button type="submit" disabled={isLoading} className={`flex items-center gap-2 bg-[#C5A059] hover:bg-white text-[#050505] px-6 py-3 text-xs tracking-widest uppercase font-bold transition-colors border-none ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}>
              {isLoading ? <><Loader2 size={16} className="animate-spin" /> Guardando...</> : 'Guardar Noticia'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}